<script setup lang="ts">
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import typescript from 'highlight.js/lib/languages/typescript';
import html from 'highlight.js/lib/languages/vbscript-html';
import { computed, inject, onMounted, ref, shallowRef, watch } from 'vue';

import { CodeSnippetProps } from './CodeSnippet.vue.d';
import Button from '../button/Button.vue';

const props = withDefaults(defineProps<CodeSnippetProps>(), {
  lang: 'json',
});
const codeSnippet = defineModel<string>('code', { default: '' });
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('json', json);
hljs.registerLanguage('html', html);

const Preset = inject<Record<string, any>>('preset', {}).codesnippet;

const textarea = ref<HTMLTextAreaElement>();
const codesnippet = ref<HTMLTableElement>();
const lineNumberWidth = shallowRef(0);
const copied = shallowRef(false);

/**
 * TODO: Dark mode for this
 * Format the code into lines and highlight each line
 */
const formattedLines = computed<{ line?: number; code?: string }[]>(() => {
  const lines = codeSnippet.value.split('\n');
  return lines.map((line, index) => {
    const highlightedLine = hljs.highlight(line, {
      language: props.lang,
    }).value;

    return {
      line: index + 1,
      code: highlightedLine,
    };
  });
});

const copyCode = (): void => {
  navigator.clipboard?.writeText(codeSnippet.value);
  copied.value = true;
  setTimeout(() => {
    copied.value = false;
  }, 1500);
};

onMounted(() => {
  updateTextareaLeftPosition();
});

const updateTextareaLeftPosition = (): void => {
  if (codesnippet.value) {
    const linenumber = codesnippet.value.querySelector(
      '[data-wv-section=linenumber]',
    ) as HTMLElement;

    lineNumberWidth.value = linenumber.offsetWidth;
  }
};

watch(formattedLines, () => {
  updateTextareaLeftPosition();
});
</script>

<template>
  <div class="relative w-full overflow-hidden">
    <Button
      v-if="codeSnippet.length && readonly"
      v-bind="Preset?.copybutton"
      :icon="copied ? 'check' : 'file-copy'"
      @click="copyCode"
      size="small"
      text
    />

    <div v-bind="Preset?.body" style="scrollbar-width: none">
      <textarea
        ref="textarea"
        v-if="!readonly"
        v-bind="Preset?.textarea"
        v-model="codeSnippet"
        :placeholder="placeholder"
        :spellcheck="false"
        :style="`left: ${lineNumberWidth}px; width: calc(100% - ${lineNumberWidth + 6}px); scrollbar-width: none;`"
      />

      <table ref="codesnippet" class="relative">
        <tbody class="relative">
          <tr :key="line" v-for="{ line, code } in formattedLines" class="h-5">
            <td
              v-bind="Preset?.linenumber"
              :class="[
                {
                  'pt-1.5': line === 1,
                  'pb-1.5': line === formattedLines.length,
                },
              ]"
              data-wv-section="linenumber"
            >
              {{ line }}
            </td>

            <td
              :class="[
                'px-1.5',
                {
                  'pt-1.5': line == 1,
                  'pb-1.5': line === formattedLines.length,
                },
              ]"
            >
              <pre v-html="code" class="font-poppins" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
